import { Injectable, inject } from '@angular/core';
import {
  CanActivate,
  CanActivateChild,
  Router,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Observable, map, take } from 'rxjs';
import { PermissionsService } from '../service/token/permissions.service';
import { AuthService } from '../service/auth/auth.service';

/**
 * Guard para proteger rutas que requieren permisos específicos
 */
@Injectable({
  providedIn: 'root',
})
export class PermissionsGuard implements CanActivate, CanActivateChild {
  private permissionsService = inject(PermissionsService);
  private authService = inject(AuthService);
  private router = inject(Router);

  /**
   * Verifica si se puede activar la ruta
   */
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    return this.checkPermissions(state.url);
  }

  /**
   * Verifica si se pueden activar las rutas hijas
   */
  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    return this.canActivate(childRoute, state);
  }

  /**
   * Comprueba si el usuario tiene permisos para acceder a la ruta
   * @param url URL intentada
   */
  private checkPermissions(url: string): Observable<boolean | UrlTree> {
    // Primero verificar autenticación
    return this.authService.isAuthenticated$.pipe(
      take(1),
      map((isAuthenticated) => {
        if (!isAuthenticated) {
          // Guardar URL para redirección después del login
          this.storeRedirectUrl(url);
          return this.router.createUrlTree(['/login']);
        }

        // Verificar permisos
        const hasAccess = this.permissionsService.canAccessRoute(url);

        if (!hasAccess) {
          // Redirigir a home si no tiene permisos
          return this.router.createUrlTree(['/home']);
        }

        return true;
      })
    );
  }

  /**
   * Guarda la URL para redirección después del login
   */
  private storeRedirectUrl(url: string): void {
    if (url && url !== '/' && !url.includes('/login')) {
      sessionStorage.setItem('redirectUrl', url);
    }
  }
}
